import { Box, Card, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography, useMediaQuery } from '@mui/material';
import React from 'react'
import { Secondary } from 'ui-components/Buttons';
import PieChart from '../../../ui-components/PieChart'
import { useTheme } from '@emotion/react';
import { handleDateValues, notEmpty } from '../../../utils';
import NoData from 'ui-components/NoData';
import RoundInformationSkeleton from './RoundInformationSkeleton';
import { FetchRoundViewDetails } from 'api/startup_applications';
import {
    RoundNameIcon,
    RoundTypeIcon,
    SecurityTypeIcon,
    TotalRoundSizeIcon,
    ValuationFloorIcon,
    ValuationCapIcon,
    MinimumChequeIcon,
    TermSheetIcon,
    CommittedFundingIcon,
    ExitHighlightIcon,
    ClockIcon,
    NetBurnIcon,
    CalendarIcon,
    RoundStatusIcon,
} from './RoundDetailIcons';

// ── Presentation ported from startup-web rounds/ViewRound/RoundDetails.js ──
// Card: radius 16, gray300 border, no shadow. Sub-cards (highlights): radius 12.
const cardSx = (theme) => ({ padding: '1.25rem 1.5rem 1.5rem', boxShadow: 'none', borderRadius: '16px', border: `1px solid ${theme.palette.customColors.gray300}`, overflow: 'hidden' });
const subCardSx = (theme) => ({ padding: '1rem', boxShadow: 'none', borderRadius: '12px', border: `1px solid ${theme.palette.customColors.gray300}`, overflow: 'hidden', marginBottom: '1.25rem' });

// Icon + title heading used inside the highlight (rich-text) cards.
const HighlightTitle = ({ children, icon: Icon }) => {
    const theme = useTheme();
    return (
        <Box display="flex" alignItems="center" gap="0.625rem" mb="0.5rem">
            {Icon && <Icon />}
            <Typography fontWeight={600} fontSize={"1rem"} color={theme.palette.customColors.g300}>
                {children}
            </Typography>
        </Box>
    );
};

// Title + divider. No Edit button: an investor cannot edit a startup's round, and
// investor-web's /rounds/edit_round route is commented out (would dump on /profile).
const CardHeader = ({ title }) => {
    const theme = useTheme();
    return <Box>
        <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} mb={"0.75rem"}>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.05rem', md: '1.2rem' } }}>
                {title}
            </Typography>
        </Box>
        <Box sx={{ borderBottom: `1px solid ${theme.palette.customColors.gray300}`, mb: '1.25rem' }} />
    </Box>
}

// Bordered list of rows, each with a leading gradient icon, label, and value.
const CardContentList = ({ data = [], style = {} }) => {
    const theme = useTheme();
    return <>
        <Grid
            container
            xs={12}
            sx={{
                border: `1px solid ${theme.palette.customColors.gray300}`,
                borderRadius: "12px",
                overflow: "hidden",
                ...style
            }}
        >
            {
                data?.map(({ label, value, icon: Icon }, index) => {
                    return (
                        <Grid
                            item
                            xs={12}
                            key={index}
                            display="flex"
                            alignItems="center"
                            gap="0.875rem"
                            sx={{
                                padding: "0.875rem 1rem",
                                borderTop: index === 0 ? 'none' : `1px solid ${theme.palette.customColors.gray300}`,
                            }}
                        >
                            {Icon && <Icon />}
                            <Typography sx={{ flex: 1, color: theme.palette.customColors.g300, fontWeight: 500 }}>
                                {label}
                            </Typography>
                            <Typography sx={{ textAlign: "end", fontWeight: 600, color: theme.palette.customColors.g300 }}>
                                {value ? value : '-'}
                            </Typography>
                        </Grid>
                    )
                })
            }
        </Grid>
    </>
}

const RichTextDisplay = ({ htmlContent, color = "" }) => {
    const [open, setOpen] = React.useState(false);

    let content = open ? htmlContent : htmlContent?.substring(0, 300);

    return (
        <>
            {content && content?.length > 0 ?
                <>
                    <div dangerouslySetInnerHTML={{ __html: content }} className='row rich-text' style={{ color: color, textAlign: 'justify' }} />
                    {htmlContent?.length > 300 &&
                        <Secondary
                            component="label"
                            role={undefined}
                            tabIndex={-1}
                            sx={{
                                backgroundColor: 'transparent',
                                border: "0",
                                padding: "0"
                            }}
                            onClick={() => setOpen(!open)}
                        > <div>{!open ? 'See more' : 'See less'}</div>
                        </Secondary>
                    }
                </>
                : "-"
            }
        </>
    );
};

const RoundInformation = ({ data = {}, round_id }) => {
    const theme = useTheme();
    const { data: roundDetails, isLoading: isLoadingRoundDetails, refetch: refetchRoundDetails } = FetchRoundViewDetails(round_id);
    const filteredRoundDetails = roundDetails ? roundDetails : {};

    React.useEffect(() => {
        roundDetails?.startup_id && delete filteredRoundDetails["startup_id"];
        roundDetails?.user_id && delete filteredRoundDetails["user_id"];
        roundDetails?._id && delete filteredRoundDetails["_id"];
    }, [])
    const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));
    const nameTypeDetails = [
        { label: "Round Name", value: roundDetails?.round_name, icon: RoundNameIcon },
        { label: "Round Type", value: roundDetails?.round_type_info?.name, icon: RoundTypeIcon },
        { label: "Type of Security", value: roundDetails?.security_type_info?.name, icon: SecurityTypeIcon },
    ]?.filter(item => notEmpty(item?.value));
    const roundSizeValuationDetails = [
        { label: "Total Round Size", value: roundDetails?.total_round_size_final, icon: TotalRoundSizeIcon },
        { label: "Round Valuation Floor", value: roundDetails?.round_valuation_final, icon: ValuationFloorIcon },
        { label: "Round Valuation Cap", value: roundDetails?.round_valuation_cap_final, icon: ValuationCapIcon },
        { label: "Minimum Cheque Size", value: roundDetails?.minimum_cheque_size_final, icon: MinimumChequeIcon },
        { label: "Have you already received any Term Sheet?", value: notEmpty(roundDetails?.received_termsheet) ? roundDetails?.received_termsheet ? "Yes" : 'No' : null, icon: TermSheetIcon },
        { label: "What funding amount has been committed so far?", value: roundDetails?.committed_funding_amount_final, icon: CommittedFundingIcon },
    ]?.filter(item => notEmpty(item?.value));
    const exitStrategyDetails = [
        { label: "Runway in Months", value: roundDetails?.runway_left > "1" ? roundDetails?.runway_left == "select_runway_left" ? null : roundDetails?.runway_left + " Months" : roundDetails?.runway_left == "1" ? roundDetails?.runway_left + " Month" : null, icon: ClockIcon },
        { label: "Monthly Net Burn", value: roundDetails?.monthly_net_burn_final ? roundDetails?.monthly_net_burn_final : null, icon: NetBurnIcon }
    ]?.filter(item => notEmpty(item?.value));

    const otherDetails = [
        { label: "Round Start Date", value: handleDateValues(roundDetails?.final_round_start_date, null), icon: CalendarIcon },
        { label: "Round Closing Date", value: handleDateValues(roundDetails?.final_round_closing_date, null), icon: CalendarIcon },
        { label: "Round Status", value: roundDetails?.round_status_info?.name ? roundDetails?.round_status_info?.name : null, icon: RoundStatusIcon }
    ]?.filter(item => notEmpty(item?.value));
    return (
        <>
            {isLoadingRoundDetails ? <RoundInformationSkeleton /> : <>
                {
                    Object.keys(filteredRoundDetails)?.length > 0 ? <>
                        <Grid item container xs={12} spacing={"1.25rem"} display={"flex"} sx={{ paddingBottom: '2rem', flexDirection: "row" }}
                            justifyContent={"space-between"}>
                            <Grid item container xs={12} sm={12} md={7} lg={7.5} spacing={"1.25rem"} alignSelf={"flex-start"}>
                                {nameTypeDetails && nameTypeDetails?.length > 0 && <Grid sx={{ gridArea: 'first', width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" }, marginInline: { xs: 'auto', sm: 0 } }} item>
                                    <Card sx={cardSx(theme)}>
                                        <CardHeader title={"Name & Type"} />
                                        <CardContentList data={nameTypeDetails} />
                                    </Card>
                                </Grid>}
                                {!isLargeScreen && <Grid sx={{ gridArea: 'aside', width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" }, marginInline: { xs: 'auto', sm: 0 } }} item>
                                    {((roundDetails?.fund_utilization_highlights && roundDetails?.fund_utilization_highlights?.length > 0) || (roundDetails?.fund_allocation_percentages && roundDetails?.fund_allocation_percentages?.length > 0)) && <Card sx={cardSx(theme)}>
                                        <CardHeader title={"Fund Utilization"} />
                                        {roundDetails?.fund_utilization_highlights && roundDetails?.fund_utilization_highlights?.length > 0 && <Card sx={subCardSx(theme)}>
                                            <HighlightTitle>Fund Utilization Highlights</HighlightTitle>
                                            <RichTextDisplay htmlContent={roundDetails?.fund_utilization_highlights} color={theme.palette.customColors.g200} />
                                        </Card>}
                                        {roundDetails?.fund_allocation_percentages && roundDetails?.fund_allocation_percentages?.length > 0 && <>
                                            <Typography fontWeight={500} fontSize={"1rem"} color={theme.palette.customColors.g200} mb={'0.625rem'}>
                                                Fund Allocation
                                            </Typography>
                                            <Card sx={{ padding: '1rem', boxShadow: 'none', borderRadius: "12px", border: `1px solid ${theme.palette.customColors.gray300}`, overflow: 'hidden' }}>
                                                <PieChart graphData={roundDetails?.fund_allocation_percentages} height='100%' width="100%" />
                                            </Card>
                                        </>}
                                    </Card>}
                                </Grid>}
                                {((roundDetails?.round_highlights && roundDetails?.round_highlights?.length > 0) || (roundSizeValuationDetails && roundSizeValuationDetails?.length > 0)) && <Grid sx={{ gridArea: 'second', width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" }, marginInline: { xs: 'auto', sm: 0 } }} item>
                                    <Card sx={cardSx(theme)}>
                                        <CardHeader title={"Round Size & Valuation"} />
                                        {(roundDetails?.round_highlights && roundDetails?.round_highlights?.length > 0) && <Card sx={subCardSx(theme)}>
                                            <HighlightTitle>Round Highlights</HighlightTitle>
                                            <RichTextDisplay htmlContent={roundDetails?.round_highlights} color={theme.palette.customColors.g200} />
                                        </Card>}
                                        {roundSizeValuationDetails && roundSizeValuationDetails?.length > 0 && <CardContentList data={roundSizeValuationDetails} />}
                                    </Card>
                                </Grid>}
                                {roundDetails?.investors && roundDetails?.investors?.length > 0 && <Grid sx={{ gridArea: 'third', width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" }, marginInline: { xs: 'auto', sm: 0 } }} item>
                                    <Card sx={cardSx(theme)}>
                                        <CardHeader title={"Current Investors"} />
                                        <Grid
                                            container
                                            xs={12}
                                            sx={{
                                                border: `1px solid ${theme.palette.customColors.gray300}`,
                                                borderRadius: "12px",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <Table>
                                                <TableHead>
                                                    <TableRow sx={{
                                                        padding: "12px 16px",
                                                        backgroundColor:
                                                            theme?.palette?.customColors?.g200
                                                    }}>
                                                        <TableCell>
                                                            <Typography fontWeight={400} color={theme?.palette?.customColors?.g50}>Investor Name</Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography fontWeight={400} color={theme?.palette?.customColors?.g50}>
                                                                Investor Email
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {roundDetails?.investors?.length > 0 && roundDetails?.investors?.map((investor, index) => {
                                                        return <TableRow
                                                            item
                                                            key={index}
                                                            xs={12}
                                                            display={"flex"}
                                                            justifyContent={"space-between"}
                                                            sx={{
                                                                padding: "12px 16px",
                                                                backgroundColor:
                                                                    index % 2 === 0 ? theme.palette.customColors.inputField : '',
                                                            }}
                                                        >
                                                            <TableCell>
                                                                <Typography fontWeight={500} color={theme?.palette?.customColors?.g300}>
                                                                    {investor?.name}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography fontWeight={400} color={theme?.palette?.customColors?.g200}>
                                                                    {investor?.email}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    })}

                                                    {roundDetails?.investors?.length == 0 && <TableRow
                                                        item
                                                        xs={12}
                                                        display={"flex"}
                                                        justifyContent={"space-between"}
                                                        sx={{
                                                            padding: "12px 16px",
                                                            backgroundColor:
                                                                theme.palette.customColors.inputField,
                                                        }}
                                                    >
                                                        <TableCell colSpan={2} marginInline={"auto"} sx={{ textAlign: 'center' }}>No Current Investors</TableCell>
                                                    </TableRow>}
                                                </TableBody>
                                            </Table>
                                        </Grid>
                                    </Card>
                                </Grid>}
                                {((roundDetails?.traction_highlights && roundDetails?.traction_highlights?.length > 0) || (roundDetails?.key_metrics && roundDetails?.key_metrics?.length > 0)) && <Grid sx={{ gridArea: 'forth', width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" }, marginInline: { xs: 'auto', sm: 0 } }} item>
                                    <Card sx={cardSx(theme)}>
                                        <CardHeader title={"Current Traction & Runway"} />
                                        {roundDetails?.traction_highlights && roundDetails?.traction_highlights?.length > 0 && <Card sx={subCardSx(theme)}>
                                            <HighlightTitle>Traction Highlight</HighlightTitle>
                                            <RichTextDisplay htmlContent={roundDetails?.traction_highlights} color={theme.palette.customColors.g200} />
                                        </Card>}
                                        {roundDetails?.key_metrics && roundDetails?.key_metrics?.length > 0 && roundDetails?.key_metrics?.map((metric, idx) => {
                                            return <React.Fragment key={idx}>
                                                <Typography fontWeight={500} mt={'0.75rem'} mb={'0.375rem'} fontSize={"1rem"} color={theme.palette.customColors.g200}>
                                                    {metric?.heading}
                                                </Typography>
                                                {metric?.properties && Array.isArray(metric?.properties) && metric?.properties?.length > 0 &&
                                                    <CardContentList data={metric?.properties?.map(item => ({ label: item?.key, ...item }))} />}
                                            </React.Fragment>
                                        })}
                                    </Card>
                                </Grid>}
                                {roundDetails?.faqs && roundDetails?.faqs?.length > 0 && <Grid sx={{ gridArea: 'fifth', width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" } }} item>
                                    <Card sx={{ ...cardSx(theme), marginInline: { xs: "0.5rem -0.5rem", sm: 0 } }}>
                                        <CardHeader title={"FAQs"} />
                                        {
                                            roundDetails?.faqs?.map(({ question, answer }, index) => {
                                                return <Card key={index} sx={subCardSx(theme)}>
                                                    <Typography fontWeight={500} fontSize={"1rem"} color={theme.palette.customColors.g200} mb={1}>
                                                        {question}
                                                    </Typography>
                                                    <RichTextDisplay htmlContent={answer} />
                                                </Card>
                                            })
                                        }
                                        {
                                            roundDetails?.faqs && roundDetails?.faqs?.length === 0 && <>
                                                <Card sx={subCardSx(theme)}>
                                                    <Typography fontWeight={500} fontSize={"1rem"} color={theme.palette.customColors.g200} textAlign={"center"}>
                                                        No FAQs available
                                                    </Typography>
                                                </Card>
                                            </>
                                        }
                                    </Card>
                                </Grid>}
                                {((roundDetails?.exit_highlights && roundDetails?.exit_highlights?.length > 0) || (exitStrategyDetails && exitStrategyDetails?.length > 0) || (otherDetails && otherDetails?.length > 0)) && <Grid sx={{ gridArea: 'sixth', width: { xs: "95vw", sm: "100%" }, maxWidth: { xs: '95%', sm: "100%" }, marginInline: { xs: 'auto', sm: 0 } }} item>
                                    <Card sx={cardSx(theme)}>
                                        <CardHeader title={"Exit Strategy & Timeline"} />
                                        {roundDetails?.exit_highlights && roundDetails?.exit_highlights?.length > 0 && <Card sx={subCardSx(theme)}>
                                            <HighlightTitle icon={ExitHighlightIcon}>Exit Highlight</HighlightTitle>
                                            <RichTextDisplay htmlContent={roundDetails?.exit_highlights} color={theme.palette.customColors.g200} />
                                        </Card>}
                                        {exitStrategyDetails && exitStrategyDetails?.length > 0 && <CardContentList data={exitStrategyDetails} />}
                                        {(otherDetails && otherDetails?.filter(item => notEmpty(item?.value))?.length > 0) &&
                                            <>
                                                <Typography fontWeight={500} mt={'0.75rem'} mb={'0.375rem'} fontSize={"1rem"} color={theme.palette.customColors.g200}>
                                                    Other Details
                                                </Typography>
                                                <CardContentList data={otherDetails?.filter(item => notEmpty(item?.value))} />
                                            </>}
                                    </Card>
                                </Grid>}
                            </Grid>
                            {
                                isLargeScreen && <Grid xs={12} sm={12} md={5} lg={4.5} sx={{ marginInline: { xs: 'auto', sm: 0 } }} item>
                                    {((roundDetails?.fund_utilization_highlights && roundDetails?.fund_utilization_highlights?.length > 0) || (roundDetails?.fund_allocation_percentages && roundDetails?.fund_allocation_percentages?.length > 0)) && <Card sx={cardSx(theme)}>
                                        <CardHeader title={"Fund Utilization"} />
                                        {roundDetails?.fund_utilization_highlights && roundDetails?.fund_utilization_highlights?.length > 0 && <Card sx={subCardSx(theme)}>
                                            <HighlightTitle>Fund Utilization Highlights</HighlightTitle>
                                            <RichTextDisplay htmlContent={roundDetails?.fund_utilization_highlights} color={theme.palette.customColors.g200} />
                                        </Card>}
                                        {roundDetails?.fund_allocation_percentages && roundDetails?.fund_allocation_percentages?.length > 0 && <>
                                            <Typography fontWeight={500} fontSize={"1rem"} color={theme.palette.customColors.g200} mb={'0.625rem'}>
                                                Fund Allocation
                                            </Typography>
                                            <Card sx={{ padding: '1rem', boxShadow: 'none', borderRadius: "12px", border: `1px solid ${theme.palette.customColors.gray300}`, overflow: 'hidden' }}>
                                                <PieChart graphData={roundDetails?.fund_allocation_percentages} height='100%' width="100%" />
                                            </Card>
                                        </>}
                                    </Card>}
                                </Grid>
                            }
                        </Grid>
                    </> : <NoData />
                }
            </>}
        </>
    )
}

export default RoundInformation
